/**
 * Grafana Faro - Real User Monitoring (RUM)
 *
 * Captures Core Web Vitals, page performance, errors, and user sessions
 * for comprehensive frontend observability.
 *
 * Architecture (v2 - March 2026):
 * - Uses built-in FetchTransport (handles batching, payload format, retries)
 * - Preflight health check before initialization — skips Faro entirely if collector is down
 * - ConsoleInstrumentation with DEBUG/TRACE disabled to prevent feedback loops
 * - TracingInstrumentation ignores Faro collector URLs to prevent trace recursion
 * - Localhost detection disables Faro for local dev regardless of APP_VUE_STAGE
 *
 * Environment Variables:
 * - APP_VUE_STAGE: Environment name (dev, stage, sandbox, demo, prod, local)
 * - APP_VUE_BRANCH: Git branch (used for environment prefix)
 *
 * Created: January 2026
 * Rewritten: March 2026 — fixed request flood, removed custom transport
 */

import { boot } from 'quasar/wrappers';
import { buildFaroCollectorUrl } from 'src/pages/api-base-url';
import {
  initializeFaro,
  ConsoleInstrumentation,
  ErrorsInstrumentation,
  FetchTransport,
  SessionInstrumentation,
  WebVitalsInstrumentation,
  LogLevel,
  type Faro,
} from '@grafana/faro-web-sdk';
import { TracingInstrumentation } from '@grafana/faro-web-tracing';
import { TransportItemType } from '@grafana/faro-core';
import { safeBoot, takePrebootErrors, getBootErrors } from 'src/utils/bootSafety';

// HMR guard to prevent multiple initializations during hot reload
let isInitialized = false;
let faroInstance: Faro | null = null;

/**
 * Preflight health check — probe the collector before initializing Faro.
 * If the collector is unreachable or returns non-2xx, returns false so we
 * skip initialization entirely (zero wasted requests).
 */
async function isCollectorHealthy(url: string): Promise<boolean> {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 3000);
    const resp = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: '{"logs":[],"exceptions":[],"measurements":[],"events":[],"traces":{"resourceSpans":[]},"meta":{}}',
      mode: 'cors',
      credentials: 'omit',
      signal: controller.signal,
    }).finally(() => clearTimeout(timeoutId));

    if (!resp.ok) {
      console.warn(`[Faro] Collector returned ${resp.status} — telemetry disabled.`, url);
      return false;
    }
    return true;
  } catch {
    console.warn('[Faro] Collector unreachable — telemetry disabled.', url);
    return false;
  }
}

/**
 * Flush any errors captured before Faro was ready.
 *
 * Sources:
 *   - `window.__prebootErrors` / localStorage `docbits_preboot_errors` —
 *     populated by the inline <script> at the top of `index.html` which
 *     installs `window.onerror` / `unhandledrejection` listeners as early
 *     as possible so errors that happen during module load reach Faro.
 *   - `getBootErrors()` — errors captured by `safeBoot()` wrappers that
 *     ran BEFORE faro.ts (boot #10). Without this, those earlier boot
 *     failures would be logged to console only and lost on page close.
 */
function flushPrebootEventsToFaro(faro: Faro): void {
  try {
    const preboot = takePrebootErrors();
    for (const evt of preboot) {
      const err = new Error(evt.message || 'preboot error');
      if (evt.stack) err.stack = evt.stack;
      faro.api.pushError(err, {
        type: evt.type,
        context: {
          source: evt.source || '',
          lineno: String(evt.lineno ?? ''),
          colno: String(evt.colno ?? ''),
          timestamp: String(evt.timestamp),
          phase: 'preboot',
        },
      });
    }

    const bootErrors = getBootErrors();
    for (const be of bootErrors) {
      const err = new Error(be.message || `boot error: ${be.bootName}`);
      if (be.stack) err.stack = be.stack;
      faro.api.pushError(err, {
        type: 'boot-error',
        context: {
          bootName: be.bootName,
          timestamp: be.timestamp,
          appVersion: be.appVersion,
          url: be.url,
          phase: 'boot',
        },
      });
    }
  } catch (err) {
    console.warn('[Faro] Failed to flush preboot/boot errors:', err);
  }
}

/**
 * Boot function for Quasar
 */
export default boot(safeBoot('faro', async ({ app, router }) => {
  // Prevent re-initialization during HMR
  if (isInitialized && import.meta.hot) {
    return;
  }

  const appEnv = process.env.APP_VUE_STAGE;
  const appVersion = process.env.APP_VERSION || '1.0.0';

  const isLocalhost =
    typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
  if (appEnv === 'local' || isLocalhost) {
    console.info('[Faro] Disabled for local development');
    return;
  }

  const collectorUrl = buildFaroCollectorUrl();
  if (!collectorUrl) {
    return;
  }

  // Preflight: check collector health BEFORE initializing Faro.
  // If the collector is down, we skip entirely — zero wasted requests.
  const healthy = await isCollectorHealthy(collectorUrl);
  if (!healthy) {
    return;
  }

  try {
    faroInstance = initializeFaro({
      app: {
        name: 'docbits-webapp',
        version: appVersion,
        environment: appEnv,
      },

      // Batching: send every 5s or when 50 items accumulate.
      // This produces ~0.2 req/s steady-state instead of per-item sends.
      batching: {
        enabled: true,
        sendTimeout: 5000,
        itemLimit: 50,
      },

      // Use the built-in FetchTransport — it handles batching, payload format,
      // and the isBatched() contract correctly (unlike our old custom transport).
      transports: [
        new FetchTransport({
          url: collectorUrl,
          // bufferSize: max queued requests when offline (default is fine)
          // concurrency: max parallel requests (default is fine)
        }),
      ],

      instrumentations: [
        // Errors: captures uncaught errors + unhandled promise rejections
        new ErrorsInstrumentation(),

        // Web Vitals: TTFB, FCP, LCP, CLS, FID, INP
        new WebVitalsInstrumentation(),

        // Console: capture warn + error only.
        // Override static disabledLevels to prevent feedback loop where Faro's own
        // console.debug messages get captured → queued → sent → more debug logs.
        (() => {
          ConsoleInstrumentation.defaultDisabledLevels = [LogLevel.TRACE, LogLevel.DEBUG];
          return new ConsoleInstrumentation();
        })(),

        // Sessions: track user journeys across page loads
        new SessionInstrumentation(),

        // Distributed tracing: propagate W3C traceparent to API calls.
        // ignoreUrls is handled automatically by FetchTransport.getIgnoreUrls()
        // which excludes the collector URL from tracing (prevents recursive traces).
        new TracingInstrumentation({
          instrumentationOptions: {
            propagateTraceHeaderCorsUrls: [
              /https:\/\/.*\.api\.docbits\.com/,
              /https:\/\/api\.docbits\.com/,
              /http:\/\/localhost:8080/,
            ],
          },
        }),
      ],

      sessionTracking: {
        enabled: true,
        persistent: true,
        samplingRate: 1.0,
      },

      // Suppress known noise from being captured as errors
      ignoreErrors: [
        /ResizeObserver/i,
        /chrome-extension:\/\//,
        /moz-extension:\/\//,
        // Dev-server module-load failures leak into Faro when the app is accessed via a
        // non-localhost address (WSL2, Docker, tunnel) so window.location.hostname is not
        // 'localhost', bypassing the early-return guard above. The error message still
        // contains the localhost:PORT URL that Vite uses internally to serve modules.
        /localhost:\d+/,
        /127\.0\.0\.1:\d+/,
      ],

      // Filter events before they reach the transport
      beforeSend: (event) => {
        // Skip debug-level logs that slipped through
        if (event.type === TransportItemType.LOG && (event.payload as any)?.level === 'debug') {
          return null;
        }

        return event;
      },

    });

    // Expose faro globally for user context updates
    (window as any).__faro = faroInstance;
    app.config.globalProperties.$faro = faroInstance;

    // Route tracking — wrapped in try-catch to never affect navigation
    if (router) {
      router.afterEach((to, from) => {
        try {
          if (faroInstance) {
            faroInstance.api.pushEvent('route_change', {
              from: from.path,
              to: to.path,
              name: (to.name as string) || 'unknown',
            });
          }
        } catch {
          // Never let telemetry errors affect navigation
        }
      });
    }

    isInitialized = true;
    console.info(`[Faro] Initialized — ${appEnv} env, collector: ${collectorUrl}`);

    // Flush any errors that happened before Faro was ready so they don't get lost.
    if (faroInstance) {
      flushPrebootEventsToFaro(faroInstance);
    }
  } catch (error) {
    console.warn('[Faro] Failed to initialize (app continues without telemetry):', error);
  }
}));

// HMR cleanup
if (import.meta.hot) {
  import.meta.hot.dispose(() => {
    isInitialized = false;
    faroInstance = null;
  });
}

/**
 * Get the Faro instance for direct usage
 */
export function getFaro(): Faro | null {
  return faroInstance || (window as any).__faro || null;
}

/**
 * Set user context after login
 */
export function setFaroUser(user: {
  id: string | number;
  email?: string;
  organizationId?: string;
  subOrgId?: string;
  role?: string;
}): void {
  try {
    const faro = getFaro();
    if (faro) {
      faro.api.setUser({
        id: String(user.id),
        email: user.email,
        attributes: {
          organization_id: user.organizationId || '',
          sub_org_id: user.subOrgId || '',
          role: user.role || '',
        },
      });
    }
  } catch {
    // Never let telemetry errors affect authentication
  }
}

/**
 * Clear user context on logout
 */
export function clearFaroUser(): void {
  try {
    const faro = getFaro();
    if (faro) {
      faro.api.resetUser();
    }
  } catch {
    // Never let telemetry errors affect logout
  }
}

/**
 * Push a custom event for business metrics
 */
export function pushFaroEvent(
  name: string,
  attributes?: Record<string, string>
): void {
  try {
    const faro = getFaro();
    if (faro) {
      faro.api.pushEvent(name, attributes);
    }
  } catch {
    // Never let telemetry errors affect application logic
  }
}

/**
 * Push an error to Faro
 */
export function pushFaroError(
  error: Error,
  context?: Record<string, string>
): void {
  try {
    const faro = getFaro();
    if (faro) {
      faro.api.pushError(error, { context });
    }
  } catch {
    // Never let telemetry errors affect error handling
  }
}

/**
 * Add a measurement (timing, count, etc.)
 */
export function pushFaroMeasurement(
  type: string,
  values: Record<string, number>,
  context?: Record<string, string>
): void {
  try {
    const faro = getFaro();
    if (faro) {
      faro.api.pushMeasurement({
        type,
        values,
        context,
      });
    }
  } catch {
    // Never let telemetry errors affect performance tracking
  }
}
